var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ScreenReaderText from '../../atoms/ScreenReaderText';
import { Small } from '../../typography';
import { FA_CHEVRON_RIGHT } from '../../../const/iconProps';
import { ENTER_KEY, SPACE_KEY } from '../../../const/keyNames';
import { Inset } from '../../spacing';
import { Badge, BodyWrapper, FlexWrapper, LeftIconWrapper, NotificationRibbon, RightIconWrapper } from './subcomponents';
import useWindowWidth from '../../customHooks/useWindowWidth';
const NotificationBar = (_a, ref) => {
    var { type, title, icon, className, onClickHandler, isTruncated, notificationCount, notificationCountLabel, subTitle, label, dataType } = _a, props = __rest(_a, ["type", "title", "icon", "className", "onClickHandler", "isTruncated", "notificationCount", "notificationCountLabel", "subTitle", "label", "dataType"]);
    const [isEllipseActive, setIsEllipseActive] = useState(null);
    const windowRef = useRef(null);
    const windowWidth = useWindowWidth();
    const isNotificationCountVisible = notificationCount > 1;
    const isClickable = isEllipseActive || isNotificationCountVisible;
    const hasSubTitle = !!(subTitle === null || subTitle === void 0 ? void 0 : subTitle.length);
    const onKeyDownHandler = (e) => {
        if (e.key === ENTER_KEY || e.key === SPACE_KEY) {
            onClickHandler();
        }
    };
    const dynamicNotificationProps = isClickable && {
        role: 'button',
        onClick: onClickHandler,
        tabIndex: 0,
        onKeyDown: onKeyDownHandler
    };
    useEffect(() => {
        var _a, _b;
        const checkEllipse = () => {
            if (windowRef === null || windowRef === void 0 ? void 0 : windowRef.current) {
                const { offsetWidth, scrollWidth } = windowRef.current;
                setIsEllipseActive(offsetWidth < scrollWidth);
            }
        };
        if (document === null || document === void 0 ? void 0 : document.fonts) {
            (_b = (_a = document === null || document === void 0 ? void 0 : document.fonts) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.then(() => {
                requestAnimationFrame(checkEllipse);
            });
        }
        else {
            requestAnimationFrame(checkEllipse);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth.width, title]);
    return (React.createElement(NotificationRibbon, Object.assign({ "aria-label": label, type: type, isClickable: isClickable, className: className }, dynamicNotificationProps, props, { ref: ref }),
        React.createElement(Inset, { horizontal: "small", vertical: "base" },
            React.createElement(FlexWrapper, null,
                icon && (React.createElement(Inset, { right: "small" },
                    React.createElement(LeftIconWrapper, { type: type }, icon))),
                React.createElement(BodyWrapper, { isTruncated: isTruncated, ref: windowRef }, title),
                isNotificationCountVisible && (React.createElement(Badge, { type: type },
                    React.createElement(Small, { weight: "semiBold" },
                        notificationCount,
                        React.createElement(ScreenReaderText, null, notificationCountLabel || 'Notifications')))),
                isClickable && (React.createElement(RightIconWrapper, { left: "small", right: "xsmall", type: type },
                    React.createElement(FontAwesomeIcon, { icon: FA_CHEVRON_RIGHT, "data-type": dataType, "data-reason": "Expand notification", "data-object": "button", "data-analytics": "clicktrackingevent" })))),
            hasSubTitle && (React.createElement(Inset, { left: "xlarge" },
                React.createElement(BodyWrapper, { hasSubTitle: true, isTruncated: isTruncated }, subTitle))))));
};
export default forwardRef(NotificationBar);
